import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listenMarketplaceItemsByStatus } from "../services/data";

const normalizeNumeric = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  const str = String(value).replace(",", ".").trim();
  if (!str) return null;
  const num = Number(str);
  return Number.isFinite(num) ? num : null;
};

const pickNumber = (...candidates) => {
  for (const candidate of candidates) {
    const parsed = normalizeNumeric(candidate);
    if (parsed !== null) return parsed;
  }
  return null;
};

const getRatingValue = (item) =>
  pickNumber(
    item?.ratingAvg,
    item?.avgRating,
    item?.rating,
    item?.score,
    item?.stats?.rating
  );

const getRatingCount = (item) =>
  pickNumber(
    item?.ratingCount,
    item?.ratingsCount,
    item?.reviewsCount,
    item?.stats?.reviews
  );

const getDownloadCount = (item) =>
  pickNumber(
    item?.downloadCount,
    item?.downloadsCount,
    item?.downloads,
    item?.importsCount,
    item?.importCount,
    item?.stats?.downloads
  );

const getThumbUrl = (item) => {
  if (!item) return null;
  if (item.releaseUrls?.thumbUrl) return item.releaseUrls.thumbUrl;
  const direct = item.thumbPath;
  if (direct && /^https?:\/\//i.test(direct)) return direct;
  return null;
};

/**
 * Marketplace public des templates publiés.
 * Props:
 *   - user
 *   - onBack()
 *   - onPublish()  -> ouvrir la page de publication
 *   - onPreview(item) -> DEPRECATED: use URL routing instead
 */
export default function Marketplace({ user, onBack, onPublish, onPreview }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [type, setType] = useState(""); // "" | "free" | "premium"
  const [lang, setLang] = useState("");
  const [jur, setJur] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 9;
  const numberFmt = useMemo(() => new Intl.NumberFormat("fr-FR"), []);

  useEffect(() => {
    const un = listenMarketplaceItemsByStatus({ status: "published" }, (arr) =>
      setItems(arr || [])
    );
    return () => un && un();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = items.slice();
    if (q) {
      base = base.filter(
        (it) =>
          (it.title || "").toLowerCase().includes(q) ||
          (it.subtitle || "").toLowerCase().includes(q) ||
          (it.category || "").toLowerCase().includes(q)
      );
    }
    if (category) base = base.filter((it) => (it.category || "") === category);
    if (type === "free") base = base.filter((it) => !it.isPremium);
    if (type === "premium") base = base.filter((it) => !!it.isPremium);
    if (lang)
      base = base.filter(
        (it) => Array.isArray(it.languages) && it.languages.includes(lang)
      );
    if (jur)
      base = base.filter(
        (it) => Array.isArray(it.jurisdictions) && it.jurisdictions.includes(jur)
      );
    return base;
  }, [items, search, category, type, lang, jur]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    // reset sur changement de filtre/recherche
    setPage(1);
  }, [search, category, type, lang, jur]);

  const fmtPrice = (it) => {
    if (!it?.isPremium) return "Gratuit";
    const cur = it.currency || "MAD";
    const p = typeof it.price === "number" ? it.price : Number(it.price || 0);
    return `${p} ${cur}`;
  };

  const handleItemClick = (item) => {
    if (!item?.id) return;
    // Navigate to detail page using React Router
    navigate(`/app/marketplace/${item.id}`);
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          <i className="fas fa-store" /> Marketplace de Templates
        </h1>
        <div className="quick-actions">
          {onBack && (
            <button className="btn btn-outline" onClick={onBack}>
              <i className="fas fa-arrow-left" /> Retour
            </button>
          )}
          {onPublish && (
            <button className="btn btn-primary" onClick={onPublish}>
              <i className="fas fa-plus" /> Publier un template
            </button>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="marketplace-filters">
        <div className="filters-header">
          <div className="filters-title">Filtres</div>
          <button
            className="btn btn-outline"
            onClick={() => {
              setCategory("");
              setType("");
              setLang("");
              setJur("");
              setSearch("");
            }}
          >
            <i className="fas fa-eraser" /> Réinitialiser
          </button>
        </div>
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">Catégorie</label>
            <select
              className="filter-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">Toutes les catégories</option>
              <option>Notaire</option>
              <option>RH</option>
              <option>Commercial</option>
              <option>Fiscalité</option>
              <option>Autre</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Type</label>
            <select
              className="filter-select"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">Tous les types</option>
              <option value="free">Gratuit</option>
              <option value="premium">Premium</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Langue</label>
            <select
              className="filter-select"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
            >
              <option value="">Toutes les langues</option>
              <option value="FR">Français</option>
              <option value="AR">Arabe</option>
              <option value="EN">Anglais</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Juridiction</label>
            <select
              className="filter-select"
              value={jur}
              onChange={(e) => setJur(e.target.value)}
            >
              <option value="">Toutes les juridictions</option>
              <option value="MA">Maroc</option>
              <option value="FR">France</option>
              <option value="EU">Union Européenne</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Recherche</label>
            <div className="search-box">
              <i className="fas fa-search search-icon" />
              <input
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un template..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Grille */}
      <div className="templates-grid">
        {pageItems.length === 0 ? (
          <div
            className="config-card"
            style={{
              gridColumn: "1 / -1",
              padding: 20,
              textAlign: "center",
              color: "#718096",
            }}
          >
            Aucun template trouvé.
          </div>
        ) : (
          pageItems.map((it) => {
            const ratingValue = getRatingValue(it);
            const ratingCount = getRatingCount(it);
            const reviewsValue =
              ratingCount != null ? Math.max(0, Math.round(ratingCount)) : null;
            const downloadCount = getDownloadCount(it);
            const downloadsValue =
              downloadCount != null ? Math.max(0, Math.round(downloadCount)) : 0;
            const reviewsLabel =
              reviewsValue != null ? numberFmt.format(reviewsValue) : null;
            const downloadsLabel = numberFmt.format(downloadsValue);
            const thumbUrl = getThumbUrl(it);

            return (
              <div className="template-card" key={it.id}>
                <div className="template-card-header">
                  <div className="template-card-title">
                    <i className="fas fa-file-alt" />
                    {it.title || "—"}
                  </div>
                  <span
                    className={`template-type ${
                      it.isPremium ? "type-premium" : "type-free"
                    }`}
                  >
                    {it.isPremium ? "Premium" : "Gratuit"}
                  </span>
                </div>
                <div className="template-card-body">
                  <div className="template-preview">
                    {thumbUrl ? (
                      <img
                        src={thumbUrl}
                        alt={`Vignette ${it.title || ""}`}
                        loading="lazy"
                      />
                    ) : (
                      <i className="fas fa-file-invoice-dollar" />
                    )}
                  </div>
                </div>
                <div className="template-card-footer">
                  <div className="template-info">
                    <div className="template-info-item">
                      <i className="fas fa-tag" />
                      <span>{it.category || "—"}</span>
                    </div>
                    <div className="template-info-item">
                      <i className="fas fa-language" />
                      <span>
                        {Array.isArray(it.languages)
                          ? it.languages.join(", ")
                          : "—"}
                      </span>
                    </div>
                    <div className="template-info-item">
                      <i className="fas fa-coins" />
                      <span>{fmtPrice(it)}</span>
                    </div>
                    <div className="template-info-item">
                      <i className="fas fa-star" />
                      <span>
                        {ratingValue != null
                          ? `${ratingValue.toFixed(1)} / 5${
                              reviewsLabel ? ` - ${reviewsLabel} avis` : ""
                            }`
                          : "Pas encore noté"}
                      </span>
                    </div>
                    <div className="template-info-item">
                      <i className="fas fa-download" />
                      <span>
                        {downloadsLabel} téléchargement
                        {downloadsValue > 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                  <div className="template-actions">
                    <button
                      className="action-btn"
                      title="Aperçu"
                      onClick={() => handleItemClick(it)}
                    >
                      <i className="fas fa-eye" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <i className="fas fa-chevron-left" />
          </button>
          {Array.from({ length: pageCount }).map((_, i) => {
            const n = i + 1;
            return (
              <button
                key={n}
                className={`pagination-btn ${n === page ? "active" : ""}`}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            );
          })}
          <button
            className="pagination-btn"
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={page === pageCount}
          >
            <i className="fas fa-chevron-right" />
          </button>
        </div>
      )}
    </div>
  );
}
